import { StudentHelperService } from './../../../service/student-helper.service';
import { SimpleDatePipe } from 'src/app/pipes/simple-date.pipe';
import { TrackingHistoryService } from './../../../../common-components/tracking-history/service/tracking-history.service';
import { User } from 'src/app/component/auth/model/user.model';
import { UserProfileService } from 'src/app/component/user-profile/service/user-profile.service';
import { PostalAddresses } from 'src/app/component/party/model/postal-addresses.model';
import { PreferredInfo } from './../../../model/preferred-info.model';
import { StudentService } from '../../../service/student.service';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Student } from 'src/app/model/student/student.model';
import { TitleCasePipe } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { City } from 'src/app/component/common-components/location/model/city.model';
import { Country } from 'src/app/component/common-components/location/model/country.model';
import { State } from 'src/app/component/common-components/location/model/state.model';

@Component({
    selector: 'edit-child-demographic-general',
    templateUrl: './edit-child-demographic-general.component.html',
    styleUrls: ['./edit-child-demographic-general.component.scss']
})
export class EditChildDemographicGeneralComponent implements OnInit {
    @Input() studentId!: number;
    @Input() editing: boolean;
    @Output() editingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    student: Student;
    user: User;

    @ViewChild('childInfoForm') childInfoForm: FormGroupDirective;
    form: UntypedFormGroup;
    birthCity: City;
    birthState: State;
    birthCountry: Country;
    birthAddress: PostalAddresses;
    preferredInfo: PreferredInfo;
    formChanges: any = {};

    isLoading: boolean = false;
    isSaving: boolean = false;

    simpleDatePipe: SimpleDatePipe = new SimpleDatePipe();

    suffixes = [
        { value: 'Jr', display: 'Jr' },
        { value: 'II', display: 'II' },
        { value: 'III', display: 'III' },
        { value: 'IV', display: 'IV' },
        { value: 'V', display: 'V' },
        { value: 'VI', display: 'VI' }
    ];
    genders = [
        { value: 'M', display: 'Male' },
        { value: 'F', display: 'Female' },
        { value: 'O', display: 'Other' },
        { value: 'B', display: '[Blank]' }
    ];
    races = [
        { value: 'American Indian or Alaska Native', display: 'American Indian or Alaska Native' },
        { value: 'Asian', display: 'Asian' },
        { value: 'Black or African American', display: 'Black or African American' },
        { value: 'Native Hawaiian or Other Pacific Islander', display: 'Native Hawaiian or Other Pacific Islander' },
        { value: 'White', display: 'White' },
        { value: 'Declined to State', display: 'Declined to State' }
    ];
    birthVerifications = [
        { value: '03', display: '03 – baptismal or church certificate' },
        { value: '04', display: '04 – birth certificate' },
        { value: '05', display: '05 – entry in family Bible' },
        { value: '06', display: '06 – hospital certificate' },
        { value: '07', display: '07 – parent’s affidavit, including verbal statement of parent or qualifying youth' },
        { value: '08', display: '08 – passport' },
        { value: '09', display: '09 – physician’s certificate' },
        { value: '10', display: '10 – previously verified school records' },
        { value: '11', display: '11 – State-issued ID' },
        { value: '12', display: '12 – driver’s license' },
        { value: '13', display: '13 – immigration document' },
        { value: '82', display: '82 – life insurance policy' },
        { value: '99', display: '99 – other' }
    ];

    medicalAlerts = [
        { value: 'A', display: 'A – Acute (short term health problem)' },
        { value: 'C', display: 'C – Chronic (long term health problem)' },
        { value: 'N', display: 'N – No identified problem' },
        { value: 'U', display: 'U – Unknown, cannot be determined' }
    ];

    promises: Array<any> = [];

    constructor(
        private fb: UntypedFormBuilder,
        private studentService: StudentService,
        private titlecasePipe: TitleCasePipe,
        private userProfileService: UserProfileService,
        private trackingHistoryService: TrackingHistoryService,
        private studentHelperService: StudentHelperService
    ) {
        this.form = fb.group({
            lastName: ['', [Validators.required]],
            secondLastName: [''],
            firstName: ['', [Validators.required]],
            middleName: [''],
            suffix: [''],
            gender: ['', [Validators.required]],
            race: ['', [Validators.required]],
            dob: ['', [Validators.required]],
            birthVerification: ['', [Validators.required]],
            medicalAlert: ['', [Validators.required]],
            latino: [false],
            multiBirth: [false],
            preferredPhoneNumber: [''],
            preferredEmail: ['', [Validators.email]]
        });
    }

    ngOnInit(): void {}

    init() {
        this.isLoading = true;

        this.show();
        this.getBirthAddress();
        this.getPreferredInfo();
        this.getUserProfile();
        Promise.all(this.promises).then((res) => {
            this.initStudent();

            this.initCurrentChanges();
            this.setCurrentChanges();

            this.isLoading = false;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.editing) {
            this.form.enable();
        } else {
            this.form.disable();
            if (changes['editing']) {
                this.init();
            }
        }
    }

    show() {
        let deferred = new Promise((resolve, reject) => {
            this.studentService.show(this.studentId).subscribe((res) => {
                if (res.student) {
                    this.student = res.student;

                    this.studentHelperService.setStudent(this.student);
                    this.studentHelperService.setStudentName(this.getStudentName(this.student));
                    this.studentHelperService.setMsdNumber(this.student.MSD_NUMBER!);
                    this.studentHelperService.setMerged(this.student.DUPLICATE_DEACTIVATE && this.student.DUPLICATE_DEACTIVATE > 0 ? 'Merged' : '');
                    this.studentHelperService.setIsMerged(this.student.IS_MERGED ? true : false);

                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
                console.log('SHOW DONE');
            });
        });

        this.promises.push(deferred);
    }

    getBirthAddress() {
        let deferred = new Promise((resolve, reject) => {
            this.studentService.getStudentBirthAddress(this.studentId).subscribe((res) => {
                if (res.birthAddress) {
                    this.birthAddress = res.birthAddress;

                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getPreferredInfo() {
        let deferred = new Promise((resolve, reject) => {
            this.studentService.getPreferredInfo(this.studentId).subscribe((res) => {
                if (res.preferredInfo) {
                    this.preferredInfo = res.preferredInfo;

                    resolve(res);
                } else if (res?.msg) {
                    this.preferredInfo = new PreferredInfo();
                    resolve(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getUserProfile() {
        let deferred = new Promise((resolve, reject) => {
            this.userProfileService.getMyProfile().subscribe((res) => {
                if (res?.user) {
                    this.user = res.user;

                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });
        this.promises.push(deferred);
    }

    getStudentName(student: Student) {
        let name: string = '';
        name += student.PERSON.LAST_NAME;
        name += student.SECOND_LAST_NAME ? ' ' + student.SECOND_LAST_NAME : '';
        name += ', ' + student.PERSON.FIRST_NAME;
        name += student.PERSON.MIDDLE_INITIAL ? ' ' + student.PERSON.MIDDLE_INITIAL : '';

        name = this.titlecasePipe.transform(name);
        return name;
    }

    initStudent() {
        this.setForm(this.student, this.preferredInfo, this.birthAddress);
    }

    setForm(student: Student, preferredInfo: PreferredInfo, birthAddress: PostalAddresses) {
        this.form.patchValue({
            lastName: student.PERSON.LAST_NAME,
            secondLastName: student.SECOND_LAST_NAME,
            firstName: student.PERSON.FIRST_NAME,
            middleName: student.PERSON.MIDDLE_INITIAL,
            suffix: student.SUFFIX,
            gender: student.SEX,
            race: student.RACE?.split(','),
            dob: student.DOB,
            birthVerification: student.BIRTH_VERIFY,
            medicalAlert: student.HEALTH_NOTE,
            latino: student.LATINO === 'Y' ? true : false,
            multiBirth: student.MULTI_BIRTH === 'Y' ? true : false,
            preferredPhoneNumber: preferredInfo?.PHONE_NUMBER,
            preferredEmail: preferredInfo?.EMAIL_ADDRESS
        });

        // Birth Address
        if (birthAddress.CITy) {
            this.birthCity = birthAddress.CITy;

            if (this.birthCity.STATE) {
                this.birthState = this.birthCity.STATE;

                if (this.birthState.COUNTRy) {
                    this.birthCountry = this.birthState.COUNTRy;
                }
            }
        }
    }

    submit(): Promise<any> {
        if (this.checkValid()) {
            this.isSaving = true;
            this.promises = [];

            let payload = this.generatePayload();

            this.saveStudent(payload.student);
            this.saveBirthAddress(payload.birthAddress, payload.student.PERSON?.PARTIES_ID);
            this.savePreferredInfo(payload.student.ID, payload.preferredInfo);
            this.createTrackingHistory();

            return new Promise((resolve, reject) => {
                Promise.all(this.promises).then(
                    (res) => {
                        this.isSaving = false;
                        resolve({ MSG: 'Child Demographics General succcessfuly updated.', SUCCESS: true });
                    },
                    (err) => {
                        this.isSaving = false;
                        reject({ MSG: 'Child Demographics General has errors.', SUCCESS: false });
                    }
                );
            });
        } else {
            return new Promise((resolve, reject) => {
                reject({ MSG: 'Child Demographics General has errors.', SUCCESS: false });
            });
        }
    }

    checkValid() {
        return this.form.valid;
    }

    generatePayload(): any {
        let payload: any = {};
        let student: Student = JSON.parse(JSON.stringify(this.student));
        let preferredInfo: PreferredInfo = new PreferredInfo();
        let birthAddress: PostalAddresses = JSON.parse(JSON.stringify(this.birthAddress));

        student.PERSON.LAST_NAME = this.form.get('lastName')?.value;
        student.SECOND_LAST_NAME = this.form.get('secondLastName')?.value;
        student.PERSON.FIRST_NAME = this.form.get('firstName')?.value;
        student.PERSON.MIDDLE_INITIAL = this.form.get('middleName')?.value;
        student.SUFFIX = this.form.get('suffix')?.value;
        student.SEX = this.form.get('gender')?.value;
        student.RACE = this.form.get('race')?.value.join(',');
        student.DOB = this.form.get('dob')?.value;
        student.BIRTH_VERIFY = this.form.get('birthVerification')?.value;
        student.HEALTH_NOTE = this.form.get('medicalAlert')?.value;
        student.LATINO = this.form.get('latino')?.value ? 'Y' : 'N';
        student.MULTI_BIRTH = this.form.get('multiBirth')?.value ? 'Y' : 'N';

        birthAddress.CITy = this.birthCity;
        birthAddress.CITy.STATE = this.birthState;
        birthAddress.CITy.STATE.COUNTRy = this.birthCountry;

        preferredInfo.PREFERRED_PHONE_NUMBER_ID = this.preferredInfo.PREFERRED_PHONE_NUMBER_ID;
        preferredInfo.PHONE_NUMBER = this.form.get('preferredPhoneNumber')?.value;
        preferredInfo.PREFERRED_EMAIL_ID = this.preferredInfo.PREFERRED_EMAIL_ID;
        preferredInfo.EMAIL_ADDRESS = this.form.get('preferredEmail')?.value;

        payload.student = student;
        payload.birthAddress = birthAddress;
        payload.preferredInfo = preferredInfo;

        return payload;
    }

    saveStudent(student: Student) {
        let deferred = new Promise((resolve, reject) => {
            this.studentService.update(student).subscribe((res) => {
                if (res?.msg) {
                    if (res.msg.SUCCESS) {
                        resolve(res);
                    } else {
                        reject(res);
                    }
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    saveBirthAddress(birthAddress: PostalAddresses, partyId: number) {
        if (birthAddress.ID) {
            let deferred = new Promise((resolve, reject) => {
                this.studentService.updateBirthAddress(birthAddress).subscribe((res) => {
                    if (res?.msg) {
                        if (res.msg.SUCCESS) {
                            resolve(res);
                        } else {
                            reject(res);
                        }
                    } else {
                        reject('err');
                    }
                });
            });

            this.promises.push(deferred);
        } else {
            let deferred = new Promise((resolve, reject) => {
                this.studentService.addBirthAddress(birthAddress, partyId).subscribe((res) => {
                    if (res?.msg) {
                        if (res.msg.SUCCESS) {
                            this.getBirthAddress();

                            resolve(res);
                        } else {
                            reject(res);
                        }
                    } else {
                        reject('err');
                    }
                });
            });

            this.promises.push(deferred);
        }
    }

    savePreferredInfo(studentId: number, preferredInfo: PreferredInfo) {
        let deferred = new Promise((resolve, reject) => {
            this.studentService.editPreferredInfo(studentId, preferredInfo).subscribe((res) => {
                if (res?.msg) {
                    if (res.msg.SUCCESS) {
                        this.preferredInfo.PREFERRED_PHONE_NUMBER_ID = res.preferredInfo.PREFERRED_PHONE_NUMBER_ID;
                        this.preferredInfo.PREFERRED_EMAIL_ID = res.preferredInfo.PREFERRED_EMAIL_ID;

                        resolve(res);
                    } else {
                        reject(res);
                    }
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    createTrackingHistory() {
        let deferred = new Promise((resolve, reject) => {
            let payload = this.getTrackingHistories();

            if (payload.editedFields.length > 0) {
                this.trackingHistoryService.create(payload).subscribe((res) => {
                    resolve(res);
                });
            } else {
                resolve({});
            }
        });

        this.promises.push(deferred);
    }

    // TRACKING HISTORY
    initCurrentChanges() {
        // Static Form Group
        this.formChanges['lastName'] = { CHANGED_FIELD: "Child's Last Name", VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['secondLastName'] = { CHANGED_FIELD: 'Second Last Name', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['firstName'] = { CHANGED_FIELD: "Child's First Name", VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['middleName'] = { CHANGED_FIELD: 'Middle Name', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['suffix'] = { CHANGED_FIELD: 'Suffix', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['gender'] = { CHANGED_FIELD: 'Gender', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['race'] = { CHANGED_FIELD: 'Race', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['dob'] = { CHANGED_FIELD: 'Date of Birth', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['birthVerification'] = { CHANGED_FIELD: 'Birth Verification', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['medicalAlert'] = { CHANGED_FIELD: 'Medical Alert', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['latino'] = { CHANGED_FIELD: 'Latino', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['multiBirth'] = { CHANGED_FIELD: 'Multi Birth', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['preferredPhoneNumber'] = { CHANGED_FIELD: 'Preferred Phone Number', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['preferredEmail'] = { CHANGED_FIELD: 'Preferred Email Address', VALUE: '', CHANGED_VALUE: '' };

        // Birth City/State/Country
        this.formChanges['birthCity'] = { CHANGED_FIELD: 'Birth City', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['birthState'] = { CHANGED_FIELD: 'Birth State', VALUE: '', CHANGED_VALUE: '' };
        this.formChanges['birthCountry'] = { CHANGED_FIELD: 'Birth Country', VALUE: '', CHANGED_VALUE: '' };
    }

    setCurrentChanges() {
        // Static Form Group
        this.formChanges['lastName'].VALUE = this.form.get('lastName')?.value;
        this.formChanges['secondLastName'].VALUE = this.form.get('secondLastName')?.value;
        this.formChanges['firstName'].VALUE = this.form.get('firstName')?.value;
        this.formChanges['middleName'].VALUE = this.form.get('middleName')?.value;
        this.formChanges['suffix'].VALUE = this.form.get('suffix')?.value;
        this.formChanges['gender'].VALUE = this.form.get('gender')?.value;
        this.formChanges['race'].VALUE = this.form.get('race')?.value.join(',');
        this.formChanges['dob'].VALUE = this.simpleDatePipe.transform(this.form.get('dob')?.value);
        this.formChanges['birthVerification'].VALUE = this.form.get('birthVerification')?.value;
        this.formChanges['medicalAlert'].VALUE = this.form.get('medicalAlert')?.value;
        this.formChanges['latino'].VALUE = this.form.get('latino')?.value ? 'Y' : 'N';
        this.formChanges['multiBirth'].VALUE = this.form.get('multiBirth')?.value ? 'Y' : 'N';
        this.formChanges['preferredPhoneNumber'].VALUE = this.form.get('preferredPhoneNumber')?.value;
        this.formChanges['preferredEmail'].VALUE = this.form.get('preferredEmail')?.value;

        // Birth City/State/Country
        if (this.birthCity) {
            this.formChanges['birthCity'].VALUE = this.birthCity.CITY_NAME;
        }
        if (this.birthState) {
            this.formChanges['birthState'].VALUE = this.birthState.STATE_NAME;
        }
        if (this.birthCountry) {
            this.formChanges['birthCountry'].VALUE = this.birthCountry.COUNTRY_NAME;
        }
    }

    updateChanges() {
        // Static Form Group
        this.formChanges['lastName'].CHANGED_VALUE = this.form.get('lastName')?.value;
        this.formChanges['secondLastName'].CHANGED_VALUE = this.form.get('secondLastName')?.value;
        this.formChanges['firstName'].CHANGED_VALUE = this.form.get('firstName')?.value;
        this.formChanges['middleName'].CHANGED_VALUE = this.form.get('middleName')?.value;
        this.formChanges['suffix'].CHANGED_VALUE = this.form.get('suffix')?.value;
        this.formChanges['gender'].CHANGED_VALUE = this.form.get('gender')?.value;
        this.formChanges['race'].CHANGED_VALUE = this.form.get('race')?.value.join(',');
        this.formChanges['dob'].CHANGED_VALUE = this.simpleDatePipe.transform(this.form.get('dob')?.value);
        this.formChanges['birthVerification'].CHANGED_VALUE = this.form.get('birthVerification')?.value;
        this.formChanges['medicalAlert'].CHANGED_VALUE = this.form.get('medicalAlert')?.value;
        this.formChanges['latino'].CHANGED_VALUE = this.form.get('latino')?.value ? 'Y' : 'N';
        this.formChanges['multiBirth'].CHANGED_VALUE = this.form.get('multiBirth')?.value ? 'Y' : 'N';
        this.formChanges['preferredPhoneNumber'].CHANGED_VALUE = this.form.get('preferredPhoneNumber')?.value;
        this.formChanges['preferredEmail'].CHANGED_VALUE = this.form.get('preferredEmail')?.value;

        // Birth City/State/Country
        if (this.birthCity) {
            this.formChanges['birthCity'].CHANGED_VALUE = this.birthCity.CITY_NAME;
        }
        if (this.birthState) {
            this.formChanges['birthState'].CHANGED_VALUE = this.birthState.STATE_NAME;
        }
        if (this.birthCountry) {
            this.formChanges['birthCountry'].CHANGED_VALUE = this.birthCountry.COUNTRY_NAME;
        }

        // Set CHANGED
        Object.keys(this.formChanges).forEach((key) => {
            this.formChanges[key].CHANGED = this.formChanges[key].VALUE !== this.formChanges[key].CHANGED_VALUE;
        });
    }

    getChanges() {
        this.updateChanges();

        let changes: any[] = [];

        Object.keys(this.formChanges).forEach((key) => {
            if (this.formChanges[key].CHANGED) {
                changes.push(this.formChanges[key]);
            }
        });

        return changes;
    }

    getTrackingHistories() {
        let trackingHistories: any[] = [];

        Object.keys(this.formChanges).forEach((key) => {
            if (this.formChanges[key].CHANGED) {
                let trackingHistory = JSON.parse(JSON.stringify(this.formChanges[key]));
                trackingHistory.CHANGED_BY = this.user.USER_ID;
                trackingHistory.CHANGED_FORM = 'Student Demographics';
                trackingHistory.CHANGED_FORM_ID = this.studentId;
                trackingHistory.CHANGED_VALUE = trackingHistory.VALUE; // VALUE holds current value, CHANGED_VALUE is used in back-end to record previous data.

                trackingHistories.push(trackingHistory);
            }
        });

        return { editedFields: trackingHistories };
    }
}
